import { Component, OnDestroy, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { FormControl, FormGroup } from '@ngneat/reactive-forms';
import { finalize, Subscription } from 'rxjs';
import { User } from 'src/app/classes/user.class';
import { AuthService } from 'src/app/services/auth/auth.service';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { FileStorage } from 'src/app/services/file.service';
import { UserService } from 'src/app/services/user/user.service';
import { Settings } from 'src/app/settings.class';
import { LocalStorage } from 'src/app/storage.class';
import { compiledVersion } from '../../../compiled-version';

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    standalone: false,
})
export class ProfileComponent implements OnInit, OnDestroy {
    saving = false;
    saved = false;
    title = '';
    currentUser: User;
    form: FormGroup<any>;
    public Settings = Settings;
    private subscriptions = new Subscription();

    constructor(
        private userService: UserService,
        private authService: AuthService,
        private confirmDialogService: ConfirmDialogService
    ) {}

    ngOnInit() {
        this.loadProfile();
    }

    loadProfile() {
        this.subscriptions.add(
            this.authService.getUserDetails().subscribe({
                next: user => {
                    this.currentUser = user;
                    this.createForm(user);
                },
                error: () => {
                    this.currentUser = new User();
                    this.createForm(this.currentUser);
                },
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    save() {
        const profilePicturePrefix = Settings.RESOURCES_ENDPOINT + FileStorage.ProfilePictures + '/';

        const profilePicture = this.form.value.profile_picture?.startsWith(profilePicturePrefix)
            ? this.form.value.profile_picture.substring(profilePicturePrefix.length)
            : this.form.value.profile_picture;

        this.saving = true;

        this.subscriptions.add(
            this.userService
                .saveProfilePicture(profilePicture)
                .pipe(finalize(() => (this.saving = false)))
                .subscribe({
                    next: user => {
                        this.currentUser = user;
                        Object.assign(LocalStorage.user, user);
                        this.form.markAsPristine();
                        this.saved = true;
                        setTimeout(() => (this.saved = false), 2000);
                    },
                    error: error => {
                        const status = error?.error?.status;

                        if (![401, 409, 500].includes(status)) {
                            this.confirmDialogService.confirm({
                                title: 'Er is iets fout gegaan',
                                text: error?.message || 'Het opslaan is niet gelukt. Probeer het opnieuw.',
                                confirm: 'Sluiten',
                            });
                        }
                    },
                })
        );
    }

    openChangesBackActionCheck(): Promise<boolean> {
        return new Promise(resolve => {
            if (this.form?.dirty) {
                this.confirmDialogService
                    .confirm({
                        title: 'Niet opgeslagen wijzigingen',
                        text: `Wilt u de niet opgeslagen wijzigingen verwerpen?`,
                        confirm: 'Hier blijven',
                        cancel: 'Wijzigingen verwerpen',
                    })
                    .then(
                        () => {
                            resolve(false);
                        },
                        () => {
                            resolve(true);
                        }
                    );
            } else {
                resolve(true);
            }
        });
    }

    private createForm(user: User) {
        this.title = user.id ? 'aanpassen' : 'toevoegen';

        this.form = new FormGroup({
            profile_picture: new FormControl(
                user.profile_picture
                    ? Settings.RESOURCES_ENDPOINT + FileStorage.ProfilePictures + '/' + user.profile_picture
                    : null
            ),
        });
    }

    protected readonly Functionalities = Functionalities;
    protected readonly compiledVersion = compiledVersion;
    protected readonly FileStorage = FileStorage;
}
